import React, { useEffect, useMemo, useState } from "react";
import {
  Breadcrumbs,
  Button,
  DateDisplay,
  Modal,
  Pill,
  Table,
  TextAreaBase,
  TextInputBase,
} from "@truevault/aquilo";
import { ActionStyle, TableDensity } from "@truevault/aquilo/src/components/table/Table";
import { DateDisplayVariant } from "@truevault/aquilo/src/components/dateDisplay/DateDisplay";
import { InformationCircleIcon, PaperAirplaneIcon } from "@heroicons/react/20/solid";
import { StandardPageWrapper } from "../../components/layout/StandardPageWrapper";
import { Routes } from "../../root/routes";
import { usePrimaryOrganization, useSelf } from "../../hooks";
import { useServiceAccountInbox, useServiceAccountSetup } from "../../hooks/useServiceAccountInbox";
import { useActionRequest } from "../../../common/hooks/api";
import { Api } from "../../../common/service/Api";
import { OrganizationPublicId } from "../../../common/service/server/Types";
import {
  ServiceAccountInboxMessageDto,
  ServiceAccountMessageCategory,
} from "../../../common/service/server/dto/ServiceAccountInboxMessageDto";
import { isServiceAccountsEnabled } from "../../util/ramsay";
import { sanitizeEmailHtml } from "../../utils/sanitizeEmailHtml";

const CATEGORY_LABELS: Record<ServiceAccountMessageCategory, string> = {
  "2fa": "2FA code",
  magic_link: "Magic link",
  password_reset: "Password reset",
  email_verification: "Email verification",
  signup_success: "Signup success",
  access_error: "Access error",
  uncategorized: "Uncategorized",
  classification_failed: "Classification failed",
};

const categoryLabel = (category: ServiceAccountMessageCategory | null): string =>
  category ? CATEGORY_LABELS[category] ?? category : "";

/** The matched vendor; a processed message with none matched none of the organization's data recipients. */
const vendorLabel = (message: ServiceAccountInboxMessageDto): string => {
  if (message.vendorName) return message.vendorName;
  return message.processed === true ? "No matching vendor" : "Not matched yet";
};

const StatusPill = ({ message }: { message: ServiceAccountInboxMessageDto }) => {
  if (message.processed === true) {
    return <Pill variant="completed">Processed</Pill>;
  }
  if (message.processed === null) {
    return <Pill variant="info">Processing</Pill>;
  }
  return <Pill variant="default">Pending</Pill>;
};

const MessageDetailModal = ({
  message,
  onClose,
}: {
  message: ServiceAccountInboxMessageDto;
  onClose: () => void;
}) => {
  const bodyHtml = useMemo(() => sanitizeEmailHtml(message.messageBody), [message.messageBody]);

  return (
    <Modal
      onClose={onClose}
      title={message.messageSubject || "(no subject)"}
      alignText="left"
      contentContainerClasses="w-full max-w-3xl"
      closeButton={true}
      showOverlay={true}
      closeOnOverlayClick={true}
    >
      <small>
        <div>
          <strong className="mr-1">From:</strong>
          <span>{message.messageFrom || "Unknown"}</span>
        </div>
        <div className="mt-1">
          <strong className="mr-1">To:</strong>
          <span>{message.messageTo}</span>
        </div>
        <div className="mt-1">
          <strong className="mr-1">Vendor:</strong>
          <span>
            {vendorLabel(message)}
            {message.dataRecipientId !== null && ` (data recipient ${message.dataRecipientId})`}
          </span>
        </div>
        <div className="mt-1 flex">
          <strong className="mr-1">Received:</strong>
          <DateDisplay date={message.createdAt} variant={DateDisplayVariant.FULL} />
        </div>
        <div className="mt-1 flex items-center gap-2">
          <strong>Status:</strong>
          <StatusPill message={message} />
          {message.processedAs && <span>{categoryLabel(message.processedAs)}</span>}
          {message.consumed && <Pill variant="primary">Consumed by Ramsay</Pill>}
        </div>
        {message.processedResult && (
          <div className="mt-1">
            <strong className="mr-1">Extracted result:</strong>
            <code className="break-all">{message.processedResult}</code>
          </div>
        )}
        <div className="mt-1 text-gray-500">
          <strong className="mr-1">Message ID:</strong>
          <span className="break-all">{message.externalId}</span>
        </div>
      </small>

      <div className="mt-4 p-3 border border-gray-300 rounded max-h-[60vh] overflow-auto">
        {bodyHtml ? (
          <div dangerouslySetInnerHTML={{ __html: bodyHtml }} />
        ) : (
          <span className="text-gray-500">(empty body)</span>
        )}
      </div>
    </Modal>
  );
};

/**
 * TrueVault-admin-only debug tool: sends a mock email into the service account ingest path via the
 * dev-only debug endpoint, so the inbox and classifier can be exercised without a real vendor email.
 */
const MockEmailPanel = ({
  organizationId,
  onSent,
}: {
  organizationId: OrganizationPublicId;
  onSent: () => void;
}) => {
  const { result: setup } = useServiceAccountSetup(organizationId);
  const to = setup?.email || "";
  const [from, setFrom] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [lastStored, setLastStored] = useState<ServiceAccountInboxMessageDto | null>(null);

  const send = useActionRequest<ServiceAccountInboxMessageDto>({
    api: () =>
      Api.serviceAccountInbox.sendMockEmail({
        to,
        from: from || undefined,
        subject: subject || undefined,
        body,
      }),
    messages: {
      defaultError:
        "Failed to send the mock email. The debug endpoint only exists in dev environments, and the address must resolve to an organization.",
    },
  });

  const canSend = to !== "" && body.trim() !== "" && !send.request.running;

  const onSend = async () => {
    const stored = await send.fetchWithResult();
    setLastStored(stored);
    setSubject("");
    setBody("");
    onSent();
  };

  return (
    <section className="mb-6 rounded-xl border border-dashed border-gray-400 bg-gray-50 p-5">
      <div className="mb-3 flex items-center gap-2">
        <h2 className="font-semibold text-gray-900">Send a mock service account email</h2>
        <Pill variant="info">TrueVault admin</Pill>
      </div>
      <p className="mb-4 text-sm text-gray-600">
        Delivers a fake vendor email through the same ingest and classification path as a real
        Postmark delivery. Dev environments only.
      </p>
      <div className="grid gap-4" style={{ maxWidth: 640 }}>
        <div>
          <label className="block text-sm font-medium leading-6 text-gray-900">To</label>
          <span className="font-mono text-sm text-gray-700">
            {to || "No service account address (is the email domain configured?)"}
          </span>
        </div>
        <TextInputBase
          field="mock-email-from"
          label="From (the classifier matches the vendor on this, the subject and the body)"
          placeholder="no-reply@klaviyo.com"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
        />
        <TextInputBase
          field="mock-email-subject"
          label="Subject"
          placeholder="Your verification code"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
        />
        <TextAreaBase
          field="mock-email-body"
          label="Body (HTML or plain text)"
          placeholder="<p>Your verification code is <b>291848</b></p>"
          rows={4}
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
        <div className="flex items-center gap-3">
          <Button color="primary" size="s" disabled={!canSend} onClick={onSend}>
            <span className="flex items-center gap-1">
              <PaperAirplaneIcon className="h-4 w-4" />
              Send mock email
            </span>
          </Button>
          {send.request.running && <span className="text-sm text-gray-500">Sending…</span>}
          {!send.request.running && lastStored && (
            <span className="text-sm text-gray-600">
              Stored and classified as{" "}
              <strong>{categoryLabel(lastStored.processedAs) || "pending"}</strong>
              {lastStored.processed === true && <> from {vendorLabel(lastStored)}</>}
              {lastStored.processedResult && (
                <>
                  {" "}
                  (result: <code>{lastStored.processedResult}</code>)
                </>
              )}
            </span>
          )}
        </div>
      </div>
    </section>
  );
};

/**
 * Basic read-only view of the organization's Ramsay service account inbox: every email received by the
 * organization's service account address, with the vendor it was matched to and the processor's verdict
 * (POL-7884, POL-7926).
 */
export const ServiceAccountInboxPage = () => {
  const [organization] = usePrimaryOrganization();
  const [self] = useSelf();
  // Sticky: `self` briefly turns undefined on refetches (useSelf has cacheTime 0), and letting the
  // flag flicker would unmount the mock email panel and wipe its form state mid-typing.
  const [isGlobalAdmin, setIsGlobalAdmin] = useState(false);
  useEffect(() => {
    if (self?.globalRole === "ADMIN") {
      setIsGlobalAdmin(true);
    }
  }, [self]);
  const enabled = isServiceAccountsEnabled(organization);
  const {
    result: messages,
    request,
    refresh,
  } = useServiceAccountInbox(organization?.id, !!organization && enabled);

  const [selectedMessage, setSelectedMessage] = useState<ServiceAccountInboxMessageDto | null>(
    null,
  );

  const columns = useMemo(
    () => [
      {
        key: "createdAt",
        header: "Received",
        render: (row: ServiceAccountInboxMessageDto) => (
          <DateDisplay date={row.createdAt} hoverForRelative={true} />
        ),
      },
      {
        key: "vendorName",
        header: "Vendor",
        render: (row: ServiceAccountInboxMessageDto) => vendorLabel(row),
      },
      {
        key: "messageFrom",
        header: "From",
        render: (row: ServiceAccountInboxMessageDto) => row.messageFrom || "",
      },
      {
        key: "messageSubject",
        header: "Subject",
        isVisuallyPrimary: true,
        render: (row: ServiceAccountInboxMessageDto) => row.messageSubject || "(no subject)",
      },
      {
        key: "processed",
        header: "Status",
        render: (row: ServiceAccountInboxMessageDto) => (
          <div className="flex flex-col items-start gap-1">
            <StatusPill message={row} />
            {row.processedAs && (
              <span className="text-xs text-gray-500">{categoryLabel(row.processedAs)}</span>
            )}
          </div>
        ),
      },
      {
        // Whether Ramsay has picked the result up is shown in the details modal
        key: "processedResult",
        header: "Result",
        render: (row: ServiceAccountInboxMessageDto) =>
          row.processedResult ? <code className="break-all">{row.processedResult}</code> : "",
      },
    ],
    [],
  );

  const rowActions = useMemo(
    () => [
      {
        label: "See Details",
        icon: <InformationCircleIcon className="h-5 w-5 text-gray-500" />,
        onClick: (message: ServiceAccountInboxMessageDto) => setSelectedMessage(message),
      },
    ],
    [],
  );

  if (organization && !enabled) {
    return (
      <StandardPageWrapper className="tw-boundary">
        <Breadcrumbs
          title="Service Account Inbox"
          source="Ramsay"
          sourceRoute={Routes.ramsay.root}
        />
        <p className="text-gray-600">
          The Service Accounts feature is not enabled for this organization.
        </p>
      </StandardPageWrapper>
    );
  }

  return (
    <StandardPageWrapper requests={[request]} className="tw-boundary">
      <Breadcrumbs
        title="Service Account Inbox"
        source="Ramsay"
        sourceRoute={Routes.ramsay.root}
        contentInlineWithTitle={
          <Button color="secondary" size="s" onClick={() => refresh()}>
            Refresh
          </Button>
        }
      />
      <p className="text-gray-600 mb-4">
        Emails sent to this organization's service account address, the address Ramsay uses to sign
        up for vendor accounts. Each message is matched to the vendor that sent it and classified
        (2FA code, magic link, password reset, ...), and the extracted result is picked up by
        Ramsay. Messages that match no vendor are left uncategorized.
      </p>

      {isGlobalAdmin && organization && (
        <MockEmailPanel organizationId={organization.id} onSent={() => refresh()} />
      )}

      <Table
        data={messages || []}
        columns={columns}
        actions={rowActions}
        actionsLabel="Details"
        tableDensity={TableDensity.COMPACT}
        actionStyle={ActionStyle.ICONS}
        keyExtractor={(row) => row.id}
        zeroState={
          <p className="text-gray-500 py-6 text-center">
            No service account emails have been received yet.
          </p>
        }
      />

      {selectedMessage && (
        <MessageDetailModal message={selectedMessage} onClose={() => setSelectedMessage(null)} />
      )}
    </StandardPageWrapper>
  );
};
